/**
 * CompanyFormSheet
 * Sheet deslizable para crear o editar una empresa.
 *  - Modo crear  → company prop omitido / null
 *  - Modo editar → company prop con la empresa existente
 *
 * En modo editar incluye una zona de peligro para dar de baja o reactivar.
 * El estilo sigue el mismo patrón que CompanyDetailModal (Sheet oscuro, full-screen en mobile).
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Building2,
  Save,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Trash2,
} from "lucide-react";
import {
  type Company,
  type CompanyDeleteSummary,
  createCompany,
  updateCompany,
  deleteCompany,
  getCompanyDeleteSummary,
} from "@/services/companiesService";
import { verifyCredentials, getCurrentUser } from "@/services/authService";
import DeleteWithAuthDialog from "@/components/DeleteWithAuthDialog";

interface CompanyFormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Empresa a editar. Omitir o pasar null para modo creación. */
  company?: Company | null;
  onSave: (company: Company) => void;
  /** Llamado tras eliminar la empresa exitosamente. */
  onDelete?: () => void;
}

export default function CompanyFormSheet({
  isOpen,
  onClose,
  company,
  onSave,
  onDelete,
}: CompanyFormSheetProps) {
  const isEditing = !!company;

  const [name, setName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // Estado del flujo de eliminación
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [deleteSummary, setDeleteSummary] = useState<CompanyDeleteSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Sync campos con la empresa cuando cambia o se abre
  useEffect(() => {
    if (isOpen) {
      setName(company?.name ?? "");
      setDeleteSummary(null);
    }
  }, [isOpen, company]);

  const handleClose = () => {
    if (isSaving || isToggling || isDeleting) return;
    onClose();
  };

  /** Abre el diálogo de eliminación cargando el resumen de recursos dependientes primero */
  const handleOpenDelete = async () => {
    if (!company) return;
    setIsLoadingSummary(true);
    try {
      const summary = await getCompanyDeleteSummary(company.id);
      setDeleteSummary(summary);
      setIsDeleteOpen(true);
    } catch {
      toast.error("No se pudo obtener el resumen de recursos. Intenta de nuevo.");
    } finally {
      setIsLoadingSummary(false);
    }
  };

  /** Verifica credenciales y, si son válidas, elimina la empresa */
  const handleDeleteConfirm = async (password: string) => {
    if (!company) return;
    const currentUser = getCurrentUser();
    if (!currentUser?.username) {
      toast.error("No se pudo obtener el usuario actual. Recarga la página.");
      throw new Error("no current user");
    }

    const isValid = await verifyCredentials(currentUser.username, password);
    if (!isValid) {
      toast.error("Contraseña incorrecta. Verifica e intenta de nuevo.");
      throw new Error("invalid credentials");
    }

    setIsDeleting(true);
    try {
      await deleteCompany(company.id);
      toast.success(`Empresa "${company.name}" eliminada correctamente`);
      setIsDeleteOpen(false);
      onClose();
      onDelete?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al eliminar la empresa");
      throw err;
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("El nombre de la empresa es obligatorio");
      return;
    }

    setIsSaving(true);
    try {
      let saved: Company;
      if (isEditing) {
        saved = await updateCompany(company.id, { name: name.trim() });
        toast.success("Empresa actualizada correctamente");
      } else {
        saved = await createCompany({ name: name.trim() });
        toast.success("Empresa creada correctamente");
      }
      onSave(saved);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al guardar la empresa"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!company) return;
    const newStatus = !company.is_active;
    setIsToggling(true);
    try {
      const updated = await updateCompany(company.id, { is_active: newStatus });
      toast.success(
        newStatus ? "Empresa reactivada correctamente" : "Empresa dada de baja correctamente"
      );
      onSave(updated);
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Error al cambiar el estado"
      );
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <>
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-slate-900 border-white/10 text-white p-0 flex flex-col h-[100dvh] overflow-hidden"
      >
        {/* Header */}
        <SheetHeader
          className="flex-shrink-0 px-6 pb-4 border-b border-white/10"
          style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white flex-shrink-0">
              <Building2 className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="text-white text-lg font-semibold leading-tight">
                {isEditing ? `Editar empresa` : "Nueva empresa"}
              </SheetTitle>
              {isEditing && (
                <div className="mt-2">
                  <Badge
                    className={
                      company.is_active
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-red-500/20 text-red-400 border-red-500/30"
                    }
                  >
                    {company.is_active ? "Activa" : "Inactiva"}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Cuerpo con scroll */}
        <ScrollArea className="flex-1 min-h-0 px-6 py-5">
          <div className="space-y-6">

            {/* Datos principales */}
            <section className="space-y-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                Información
              </p>

              <div className="space-y-2">
                <Label htmlFor="company-name" className="text-white/70 text-sm">
                  Nombre <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="company-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Mi Empresa S.A."
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/30
                             focus:bg-white/10 focus:border-white/40"
                  disabled={isSaving || isToggling}
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                  autoFocus
                />
              </div>
            </section>

            {/* Zona de peligro — solo en edición */}
            {isEditing && (
              <>
                <Separator className="bg-white/10" />

                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                    Estado de la empresa
                  </p>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      {company.is_active ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                      )}
                      <p className="text-sm text-white/70">
                        {company.is_active
                          ? "La empresa está activa y operativa."
                          : "La empresa está dada de baja. Sus sucursales y usuarios no podrán operar."}
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleToggleActive}
                      disabled={isToggling || isSaving}
                      className={
                        company.is_active
                          ? "w-full border-red-500/40 text-red-400 hover:bg-red-500/10 hover:border-red-500/60 bg-transparent"
                          : "w-full border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500/60 bg-transparent"
                      }
                    >
                      {isToggling ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : company.is_active ? (
                        <AlertTriangle className="h-4 w-4 mr-2" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      )}
                      {company.is_active ? "Dar de baja" : "Reactivar empresa"}
                    </Button>
                  </div>
                </section>

                {/* Zona de eliminación permanente */}
                <Separator className="bg-white/10" />

                <section className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-red-400/70">
                    Zona de peligro
                  </p>

                  <div className="rounded-xl border border-red-900/50 bg-red-950/20 p-4 space-y-3">
                    <p className="text-sm text-white/60">
                      Eliminar la empresa borra permanentemente todas sus
                      sucursales, dueños asignados, gerentes y dispositivos.
                      Esta acción <span className="text-red-400 font-semibold">no se puede deshacer</span>.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleOpenDelete}
                      disabled={isSaving || isToggling || isDeleting || isLoadingSummary}
                      className="w-full border-red-600/50 text-red-400 hover:bg-red-600/15 hover:border-red-500 bg-transparent"
                    >
                      {isLoadingSummary ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      Eliminar empresa permanentemente
                    </Button>
                  </div>
                </section>
              </>
            )}
          </div>
        </ScrollArea>

        {/* Footer fijo */}
        <SheetFooter
          className="flex-shrink-0 flex flex-row justify-center sm:justify-end gap-2 px-6 py-4
                     border-t border-white/10 bg-slate-900"
        >
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving || isToggling || isDeleting}
            className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white bg-transparent"
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isToggling || isDeleting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {isEditing ? "Guardar cambios" : "Crear empresa"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>

    {/* Diálogo de eliminación con re-autenticación */}
    {isEditing && company && deleteSummary && (
      <DeleteWithAuthDialog
        isOpen={isDeleteOpen}
        onClose={() => !isDeleting && setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        resourceType="empresa"
        resourceName={company.name}
        cascadeItems={[
          { label: "Sucursales",       count: deleteSummary.branches, icon: "branch"  },
          { label: "Dueños",           count: deleteSummary.owners,   icon: "owner"   },
          { label: "Gerentes",         count: deleteSummary.managers, icon: "manager" },
          { label: "Dispositivos POS", count: deleteSummary.devices,  icon: "device"  },
        ]}
        isDeleting={isDeleting}
      />
    )}
    </>
  );
}
